// Simple Update Notification Service
// Safe implementation that won't break the app

class UpdateNotificationService {
  constructor() {
    this.currentVersion = process.env.VUE_APP_VERSION || '1.0.0';
    this.lastCheckedVersion = localStorage.getItem('last_checked_version');
    this.updateNotifications = JSON.parse(localStorage.getItem('update_notifications') || '[]');
    this.checkInterval = null;
    this.listeners = [];
  }

  // Initialize the service
  init() {
    console.log(`📱 Update Service initialized - Current version: ${this.currentVersion}`);
    
    // PRODUCTION FIX: Clear any existing update loop issues
    this.fixUpdateLoopIssues();
    
    // Only show "updated" notifications if this is genuinely a new version
    // and we haven't been in a loop
    const isGenuineUpdate = this.isGenuineVersionUpdate();
    
    if (isGenuineUpdate) {
      console.log(`🆕 Genuine version update detected: ${this.lastCheckedVersion} → ${this.currentVersion}`);
      this.addUpdateNotification({
        version: this.currentVersion,
        type: 'updated',
        message: `App updated to version ${this.currentVersion}`,
        timestamp: new Date().toISOString()
      });
    }
    
    // Always update the last checked version to current
    localStorage.setItem('last_checked_version', this.currentVersion);

    // Start periodic version checking (every 10 minutes - safe interval)
    this.startPeriodicCheck();

    // Listen for service worker updates (if available)
    this.listenForServiceWorkerUpdates();
  }

  // Add update notification to the bell
  addUpdateNotification(notification) {
    const newNotification = {
      id: Date.now(),
      ...notification,
      read: false
    };

    this.updateNotifications.unshift(newNotification);
    
    // Keep only last 5 notifications
    this.updateNotifications = this.updateNotifications.slice(0, 5);
    
    localStorage.setItem('update_notifications', JSON.stringify(this.updateNotifications));
    
    // Notify listeners (header component)
    this.notifyListeners();
    
    console.log('🔔 Update notification added:', newNotification);
  }

  // Get all update notifications
  getUpdateNotifications() {
    return this.updateNotifications;
  }

  // Get unread count for bell badge
  getUnreadCount() {
    return this.updateNotifications.filter(n => !n.read).length;
  }

  // Mark notification as read
  markAsRead(notificationId) {
    const notification = this.updateNotifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      localStorage.setItem('update_notifications', JSON.stringify(this.updateNotifications));
      this.notifyListeners();
    }
  }

  // Mark all as read
  markAllAsRead() {
    this.updateNotifications.forEach(n => n.read = true);
    localStorage.setItem('update_notifications', JSON.stringify(this.updateNotifications));
    this.notifyListeners();
  }

  // Clear old notifications
  clearOldNotifications() {
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    this.updateNotifications = this.updateNotifications.filter(n => 
      new Date(n.timestamp) > oneWeekAgo
    );
    localStorage.setItem('update_notifications', JSON.stringify(this.updateNotifications));
    this.notifyListeners();
  }

  // Subscribe to notifications
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  // Notify all listeners
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback({
          notifications: this.updateNotifications,
          unreadCount: this.getUnreadCount()
        });
      } catch (error) {
        console.error('Error notifying update listener:', error);
      }
    });
  }

  // Start periodic checking (safe interval)
  startPeriodicCheck() {
    // Check every 10 minutes for version changes
    this.checkInterval = setInterval(() => {
      this.checkForUpdates();
    }, 10 * 60 * 1000);
  }

  // Stop periodic checking
  stopPeriodicCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  // Check for updates by comparing version
  async checkForUpdates() {
    try {
      // Simple approach: check if the main HTML has changed
      const response = await fetch(`${window.location.origin}/?t=${Date.now()}`, {
        method: 'HEAD',
        cache: 'no-cache'
      });

      if (response.ok) {
        // Check ETag or Last-Modified to detect changes
        const etag = response.headers.get('etag');
        const lastModified = response.headers.get('last-modified');
        const cacheKey = `${etag}-${lastModified}`;
        const lastCacheKey = localStorage.getItem('app_cache_key');

        if (lastCacheKey && lastCacheKey !== cacheKey && cacheKey !== 'null-null') {
          console.log('🔄 App update detected via cache headers');
          this.addUpdateNotification({
            version: 'New Version',
            type: 'available',
            message: 'A new version of the app is available. Refresh to update.',
            timestamp: new Date().toISOString()
          });
        }

        localStorage.setItem('app_cache_key', cacheKey);
      }
    } catch (error) {
      console.log('Update check failed (this is normal):', error.message);
    }
  }

  // Listen for service worker updates (if available)
  listenForServiceWorkerUpdates() {
    // Listen for custom service worker events
    window.addEventListener('sw-updated', (event) => {
      console.log('🔄 Service worker update detected');
      this.addUpdateNotification({
        version: event.detail?.version || 'New Version',
        type: 'ready',
        message: `Version ${event.detail?.version || 'New'} is ready. Refresh to update.`,
        timestamp: new Date().toISOString()
      });
    });

    // Listen for app update events from backend notifications
    window.addEventListener('app-update-notification', (event) => {
      console.log('📡 Backend update notification received');
      this.addUpdateNotification({
        version: event.detail?.version || 'New Version',
        type: 'broadcast',
        message: `New version ${event.detail?.version || ''} is available!`,
        timestamp: new Date().toISOString()
      });
    });
  }

  // PRODUCTION FIX: Detect and fix update loop issues
  fixUpdateLoopIssues() {
    const now = Date.now();
    const lastInit = localStorage.getItem('service_last_init');
    const initCount = parseInt(localStorage.getItem('service_init_count') || '0');
    
    // If service initialized more than 3 times in last 5 minutes, we have a loop
    if (lastInit && (now - parseInt(lastInit)) < 5 * 60 * 1000 && initCount > 3) {
      console.log('🔧 FIXING: Update loop detected, clearing all tracking data');
      this.clearAllVersionTracking();
      localStorage.setItem('loop_fixed_at', now.toString());
    }
    
    // Reset counter if it's been more than 5 minutes
    if (!lastInit || (now - parseInt(lastInit)) > 5 * 60 * 1000) {
      localStorage.setItem('service_init_count', '1');
    } else {
      localStorage.setItem('service_init_count', (initCount + 1).toString());
    }
    
    localStorage.setItem('service_last_init', now.toString());
  }

  // Check if this is a genuine version update (not a loop)
  isGenuineVersionUpdate() {
    // If we just fixed a loop, don't show update notifications for a while
    const loopFixedAt = localStorage.getItem('loop_fixed_at');
    if (loopFixedAt && (Date.now() - parseInt(loopFixedAt)) < 10 * 60 * 1000) {
      console.log('🔧 Skipping update notification - recently fixed loop');
      return false;
    }
    
    // No previous version = first time user, don't show "updated" notification
    if (!this.lastCheckedVersion) {
      return false;
    }
    
    // Same version = no update
    if (this.lastCheckedVersion === this.currentVersion) {
      return false;
    }
    
    // Check if we've already shown notification for this version
    const shownVersions = JSON.parse(localStorage.getItem('shown_update_versions') || '[]');
    if (shownVersions.includes(this.currentVersion)) {
      return false;
    }
    
    // Track that we're showing notification for this version
    shownVersions.push(this.currentVersion);
    localStorage.setItem('shown_update_versions', JSON.stringify(shownVersions.slice(-5)));
    
    return true;
  }

  // Clear all version tracking data
  clearAllVersionTracking() {
    const keysToRemove = [
      'last_checked_version',
      'shown_update_versions', 
      'update_notifications',
      'app_cache_key',
      'app_just_updated',
      'service_last_init',
      'service_init_count'
    ];
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    this.updateNotifications = [];
    console.log('🗑️ Cleared all version tracking data');
  }

  // Manual refresh method
  refreshApp() {
    console.log('🔄 Refreshing app...');
    
    // Mark current version as fully handled
    const shownVersions = JSON.parse(localStorage.getItem('shown_update_versions') || '[]');
    if (!shownVersions.includes(this.currentVersion)) {
      shownVersions.push(this.currentVersion);
      localStorage.setItem('shown_update_versions', JSON.stringify(shownVersions.slice(-5)));
    }
    
    // Save current version as the last checked version
    localStorage.setItem('last_checked_version', this.currentVersion);
    
    // Clear update notifications that triggered this refresh
    this.updateNotifications = this.updateNotifications.filter(n => 
      n.type !== 'available' && n.type !== 'ready' && n.type !== 'updated'
    );
    localStorage.setItem('update_notifications', JSON.stringify(this.updateNotifications));
    
    // Mark that user manually refreshed (prevents auto-notifications)
    localStorage.setItem('user_manual_refresh', Date.now().toString());
    
    // Reset init counter to prevent false loop detection
    localStorage.removeItem('service_init_count');
    localStorage.removeItem('service_last_init');
    
    // Refresh the page
    window.location.reload();
  }

  // Destroy the service
  destroy() {
    this.stopPeriodicCheck();
    this.listeners = [];
  }

  // DEBUG METHODS - Remove in production
  debugVersionInfo() {
    console.log('🔍 VERSION DEBUG INFO:');
    console.log('Current version:', this.currentVersion);
    console.log('Last checked version:', this.lastCheckedVersion);
    console.log('Shown versions:', JSON.parse(localStorage.getItem('shown_update_versions') || '[]'));
    console.log('Update notifications:', this.updateNotifications);
    console.log('App cache key:', localStorage.getItem('app_cache_key'));
    console.log('Just updated flag:', localStorage.getItem('app_just_updated'));
  }

  clearAllVersionData() {
    console.log('🗑️ Clearing all version tracking data...');
    this.clearAllVersionTracking();
    this.notifyListeners();
    console.log('✅ All version data cleared');
  }

  simulateVersionUpdate(newVersion) {
    console.log(`🎭 Simulating update from ${this.currentVersion} to ${newVersion}`);
    localStorage.setItem('last_checked_version', this.currentVersion);
    this.currentVersion = newVersion;
    this.init();
  }
}

// Create singleton instance
const updateNotificationService = new UpdateNotificationService();

export default updateNotificationService; 